import { ThemeEvents } from '@theme/events';

let refreshFrame = null;
let refreshTimer = null;
let latestSections = null;

const observedCartComponents = new WeakSet();

/**
 * Carefone cart display rule:
 * If a product title contains a hyphen, show only everything before the FIRST hyphen.
 */
function trimCartTitles(root = document) {
  if (!('querySelectorAll' in root)) return;

  for (const title of root.querySelectorAll('.cart-items__title')) {
    if (!(title instanceof HTMLElement)) continue;

    const visibleTitle = (title.textContent || '').trim();
    const hyphenIndex = visibleTitle.indexOf('-');
    if (hyphenIndex === -1) continue;

    const shortTitle = visibleTitle.slice(0, hyphenIndex).trim();
    if (!shortTitle || shortTitle === visibleTitle) continue;

    title.textContent = shortTitle;
  }
}

/**
 * Horizon can temporarily recycle a media cell while morphing normal cart rows.
 * Reconcile media from Shopify's fresh section HTML by the actual line key.
 * Customiser thumbnails are explicitly excluded because their _ImgLink image has
 * its own loading/fallback state and should never be replaced by this generic fix.
 */
function repairCartMedia(sections = latestSections) {
  if (!sections || typeof sections !== 'object') return;

  for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (!(component instanceof HTMLElement)) continue;

    const sectionId = component.dataset.sectionId;
    if (!sectionId) continue;

    const sectionHtml = sections[sectionId];
    if (typeof sectionHtml !== 'string' || !sectionHtml) continue;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sourceRows = new Map();

    for (const sourceRow of parsed.querySelectorAll('.cart-items__table-row[data-key]')) {
      if (!(sourceRow instanceof HTMLElement)) continue;
      const key = sourceRow.dataset.key;
      if (key) sourceRows.set(key, sourceRow);
    }

    for (const liveRow of component.querySelectorAll('.cart-items__table-row[data-key]')) {
      if (!(liveRow instanceof HTMLElement)) continue;

      const key = liveRow.dataset.key;
      if (!key) continue;

      const sourceRow = sourceRows.get(key);
      if (!(sourceRow instanceof HTMLElement)) continue;

      const liveMedia = liveRow.querySelector('.cart-items__media');
      const sourceMedia = sourceRow.querySelector('.cart-items__media');
      if (!(liveMedia instanceof HTMLElement) || !(sourceMedia instanceof HTMLElement)) continue;

      const customMedia =
        liveMedia.querySelector('.cart-items__media-container--custom') ||
        sourceMedia.querySelector('.cart-items__media-container--custom');

      // Customised products use the private _ImgLink line-item property. Their
      // image can briefly enter a browser loading/error state during a cart morph.
      // Re-cloning that media cell resets the image state and can leave the
      // product fallback visible until another render, so never touch it here.
      if (customMedia) continue;

      const liveImage = liveMedia.querySelector('img');
      const sourceImage = sourceMedia.querySelector('img');

      const imageFailed =
        liveImage instanceof HTMLImageElement &&
        liveImage.complete &&
        liveImage.naturalWidth === 0;

      const imageHidden =
        liveImage instanceof HTMLElement &&
        (liveImage.hidden || liveImage.style.display === 'none' || liveImage.style.visibility === 'hidden');

      const mediaDiffers = liveMedia.innerHTML !== sourceMedia.innerHTML;
      const imagePresenceDiffers = Boolean(liveImage) !== Boolean(sourceImage);

      if (mediaDiffers || imagePresenceDiffers || imageFailed || imageHidden) {
        liveMedia.replaceWith(sourceMedia.cloneNode(true));
      }
    }
  }
}

function wakeModelSwitchers() {
  for (const dialog of document.querySelectorAll('cart-model-change-dialog')) {
    if (!(dialog instanceof HTMLElement)) continue;

    const marker = document.createElement('span');
    marker.hidden = true;
    marker.dataset.cartModelSync = '';
    dialog.append(marker);
    marker.remove();
  }
}

const titleObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    const target = mutation.target;
    if (target instanceof Element) trimCartTitles(target.closest('cart-items-component') || target);
  }
});

function observeCartComponents() {
  for (const component of document.querySelectorAll('cart-items-component')) {
    if (!(component instanceof HTMLElement) || observedCartComponents.has(component)) continue;

    observedCartComponents.add(component);
    titleObserver.observe(component, { childList: true, subtree: true });
  }
}

function refreshModelUI() {
  observeCartComponents();
  repairCartMedia();
  trimCartTitles();
  wakeModelSwitchers();
  queueMicrotask(trimCartTitles);
}

function scheduleModelRefresh(event) {
  const sections = event?.detail?.data?.sections;
  if (sections && typeof sections === 'object') latestSections = sections;

  queueMicrotask(refreshModelUI);

  if (refreshFrame !== null) cancelAnimationFrame(refreshFrame);
  refreshFrame = requestAnimationFrame(() => {
    refreshFrame = null;
    refreshModelUI();
  });

  if (refreshTimer !== null) clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    refreshTimer = null;
    refreshModelUI();
  }, 120);
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleModelRefresh);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleModelRefresh(), { once: true });
} else {
  scheduleModelRefresh();
}
